/**
 * Precedent: the measured screen lock — `apps/portal/web/src/pages/admin/UsersTab.tsx:374-439`
 * and the same block on `pages/portfolio_definition/executive/ExecutiveDashboard.tsx:893-920`.
 * Cloned, not imported. (`UsersTab` is the platform's own area, not APR's, but a hook does not
 * exist there to import — the block is repeated per screen across twenty-odd screens, so this
 * module factors it once for its own screens and nothing outside this folder is touched.)
 *
 * WHY IT IS MEASURED AND NOT `height: 100%`. CLAUDE.md, "A screen header never scrolls":
 * `<main>` carries `min-height: 100vh` and then GROWS with whatever it holds, so a percentage
 * resolves against nothing and the window scrolls — taking the heading, the filters and the
 * buttons with it. Both numbers this needs are things the browser already knows, so both are
 * read from the DOM:
 *
 *   height = innerHeight − element.getBoundingClientRect().top − computed paddingBottom of <main>
 *
 * NOTHING HERE IS A TYPED CONSTANT. `UsersTab.tsx:408-417` records what happens when it is:
 * subtracting `LAYOUT.footerHeight` (40) where the real reservation is `footerHeight + 24`
 * (64) left the page 16px scrollable. Asking the element for its computed padding cannot
 * drift — change the token, add a banner, and this follows.
 *
 * A CALLBACK REF, NOT `useRef`. Every screen in this module returns a loading state before its
 * table exists, so an effect keyed on a `useRef` runs once against `null`, measures nothing,
 * and attaches the observer to nothing. `UsersTab.tsx:388-401`: "A refresh 'put it back'
 * because it had never been applied at all." A callback ref fires when the node attaches,
 * whenever that is.
 *
 * ALSO REQUIRED, AND NOT THIS HOOK'S JOB — the stylesheet's half of the bargain:
 *   · `min-height: 0` on every flex item between the locked root and the scroller, because an
 *     item defaults to `min-height: auto` and refuses to shrink below its content;
 *   · `flex-shrink: 0` on ROWS inside a flex-column scroller, or the browser squashes them
 *     instead of scrolling (Recommendation Scoring's threshold cards lost their text that way);
 *   · NO bottom padding on the locked root — `<main>`'s padding already reserves the footer
 *     band and the measurement already subtracts it, so paying again leaves a white strip.
 *     That is `.pd-screen--locked` (`portfolio_definition/screens.css:28-46`), and it is why
 *     `.erp-app .page` (source-vocabulary.css) carries none.
 */
import { useEffect, useState } from 'react';

export interface ScreenLock {
  /** Attach to the element that must fill the remaining viewport. A CALLBACK ref. */
  readonly setRoot: (node: HTMLDivElement | null) => void;
  /** Spread onto that element's `style`. Empty until the first measurement lands. */
  readonly rootStyle: { height?: number };
}

/**
 * @param floor The smallest height worth locking to, below which scrolling the window is
 *   less bad than a 40px data region. `UsersTab` floors at 240 and the Executive Dashboard
 *   at 320; a screen passes whichever its own content warrants.
 */
export function useScreenLock(floor = 240): ScreenLock {
  const [root, setRoot] = useState<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | null>(null);

  useEffect(() => {
    if (!root) return;
    const main = root.closest('main');

    const measure = () => {
      const top = root.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(floor, Math.floor(window.innerHeight - top - inset));
      // Ignore sub-pixel churn, so observing <main> cannot feed itself a resize loop.
      setHeight((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };

    measure();
    window.addEventListener('resize', measure);

    /*
     * Observe EVERYTHING ABOVE the data, not one element of it. The region's top moves when
     * a toolbar wraps to a second line, when an error alert opens above the table, and when
     * the shell's own padding changes. Watching one of the three is how this ends up right
     * in one state and wrong in another (`UsersTab.tsx:426-434`).
     *
     * Observing the root itself is safe BECAUSE of the sub-pixel guard above and because the
     * measurement reads `top`, not the root's own height.
     */
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [root, floor]);

  return { setRoot, rootStyle: height === null ? {} : { height } };
}
